import React, { memo } from 'react'
import { NavLink } from 'react-router-dom'
import { ListItem, ListItemButton, Theme, Typography } from '@mui/material'
import { LinkMenuItemProps } from '../model/types'
import { brandedTokens } from '@/theme/tokens'

export const LinkMenuItem: React.FC<LinkMenuItemProps> = memo(
    ({
        to,
        children,
        onClick,
        className,
        linkClassName,
        hidden = false,
        nested = false,
        exact = false,
        matchSearch = false,
    }) => {
        return (
            <ListItem
                disablePadding
                className={className}
                data-cy="menu-item"
                data-cy-id={to}
            >
                <ListItemButton
                    component={NavLink}
                    to={to}
                    onClick={onClick}
                    className={linkClassName}
                    exact={exact}
                    isActive={(
                        match: unknown,
                        location: { pathname: string; search: string }
                    ) => {
                        if (!match) return false
                        if (!matchSearch) return true

                        const target = new URL(to, window.location.origin)

                        return (
                            location.pathname === target.pathname &&
                            location.search === target.search
                        )
                    }}
                    sx={(theme: Theme) => ({
                        px: nested ? theme.spacing(4) : theme.spacing(2),
                        py: theme.spacing(1),
                        textDecoration: 'none',
                        color: hidden
                            ? theme.palette.text.secondary
                            : theme.palette.text.primary,
                        width: '100%',
                        boxSizing: 'border-box',
                        position: 'relative',
                        '&.menu-head-item .MuiTypography-root': {
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            border: `1px solid theme.palette.secondary.light}`,
                            color: hidden
                                ? theme.palette.text.disabled
                                : theme.palette.text.primary,
                            fontWeight: 500,
                            py: theme.spacing(1.15),
                            '&:hover': {
                                color: hidden
                                    ? theme.palette.text.disabled
                                    : theme.palette.text.primary,
                                bgcolor: 'inherit',
                            },
                        },
                        '&.active, &.active:hover': {
                            bgcolor: brandedTokens.variables.secondaryScale[30],
                        },
                        '&.active': {
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                width: theme.spacing(0.625),
                                height: '100%',
                                bgcolor:
                                    brandedTokens.variables.secondaryScale[80],
                            },
                        },
                        '&.link-item:hover:not(.active), .menu-head-item:hover:not(.active) ':
                            {
                                bgcolor:
                                    brandedTokens.variables.secondaryScale[10],
                            },
                        '&.dashboard': {
                            fontWeight: 600,
                            py: theme.spacing(1.15),
                        },
                    })}
                >
                    <Typography
                        sx={{
                            fontSize: 'inherit',
                            fontWeight: 'inherit',
                            fontFamily: 'inherit',
                        }}
                    >
                        {children}
                    </Typography>
                </ListItemButton>
            </ListItem>
        )
    }
)

LinkMenuItem.displayName = 'LinkMenuItem'
